'use client';

import React, { useEffect, useMemo, useState } from 'react';
import './globals.css';
import { CoachingSession, Session, SessionStage } from '@prisma/client';
import { usePathname, useParams, useRouter, useSearchParams } from 'next/navigation';
import { SessionProvider } from 'next-auth/react';
import { isCharacterNameValid } from '@/utils/utils';
import { getCoachingSession } from './api/sessions/coaching-session/getSession/utils';
import { getSession } from './api/sessions/getSession/utils';
import { fetchSession } from './api/sessions/utils';

interface SessionContextInterface {
	session: Session | null;
	setNewSession: (session: Session) => void;
}

export const SessionContext = React.createContext<SessionContextInterface>({
	session: null,
	setNewSession: () => {},
});

export default function WithSession({ children }: { children: React.ReactNode }) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { characterName, moduleId, coachingSessionId, sessionId } = useParams() as {
		characterName: string;
		moduleId: string;
		coachingSessionId: string;
		sessionId: string; // authoring
	};
	const [isFetchingSession, setIsFetchingSession] = useState(false);
	const [session, setSession] = useState<Session | null>(null);
	const [coachingSession, setCoachingSession] = useState<CoachingSession | null>(null);
	const [authoringCharacter, setAuthoringCharacter] = useState<string | null>(null);

	const sessionContextValue = useMemo(
		() => ({
			session,
			setNewSession: setSession,
		}),
		[session],
	);

	const routeBySessionStage = (stage: SessionStage) => {
		const routes: Partial<Record<SessionStage, string>> = {
			[SessionStage.STARTED]: `/practice/${characterName}`,
			[SessionStage.IN_PROGRESS]: `/practice/${characterName}/call`,
			[SessionStage.COMPLETED]: `/practice/${characterName}/assessment`,
		};

		const expectedRoute = routes[stage];
		if (!expectedRoute || pathname === expectedRoute) {
			return;
		}

		router.replace(expectedRoute);
	};

	const routeByVoiceCoachingSessionStage = (stage: SessionStage) => {
		const routes: Partial<Record<SessionStage, string>> = {
			[SessionStage.IN_PROGRESS]: `/coach/voice/${coachingSessionId}`,
			[SessionStage.COMPLETED]: `/coach/voice/${coachingSessionId}/next-steps`,
		};
		const expectedRoute = routes[stage];

		if (!expectedRoute || pathname === expectedRoute) {
			return;
		}

		router.replace(expectedRoute);
	};

	// on every navigation within the session, check the current stage and redirect to the correct page if necessary
	useEffect(() => {
		if (isFetchingSession || !pathname.startsWith('/practice/')) return;

		if (!session || !session.stage) {
			return;
		}

		if (!searchParams.has('author')) {
			routeBySessionStage(session.stage);
		}
	}, [pathname, session?.stage, isFetchingSession]);

	// find or create when the user first lands on a practice or module page
	useEffect(() => {
		if (isFetchingSession) {
			return;
		}
		if (!characterName && !moduleId) {
			return;
		}

		if ((pathname.startsWith('/practice/') || pathname.startsWith('/module/')) && !searchParams.has('author')) {
			setIsFetchingSession(true);

			const findOrCreateSession = async (isAssessment: boolean) => {
				try {
					const res = await fetch('/api/sessions', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							scenarioId: characterName ?? moduleId,
							isAssessment,
						}),
					});

					if (!res.ok) {
						throw new Error(`HTTP error! status: ${res.status}`);
					}

					const newSession = await res.json();
					setSession(newSession);
				} catch (error) {
					console.error(error, {
						extra: {
							characterName,
							moduleId,
							coachingSessionId,
						},
					});
					console.error('Failed to create module session:', error);
				} finally {
					setIsFetchingSession(false);
				}
			};
			const isAssessment = pathname.includes('assessment');
			findOrCreateSession(isAssessment);
		}
	}, [characterName, moduleId, pathname]);

	useEffect(() => {
		if (isFetchingSession) {
			return;
		}

		if (pathname.startsWith('/author')) {
			if (sessionId) {
				setIsFetchingSession(true);

				const findAuthoringSession = async () => {
					try {
						const newAuthoringSession = await getSession(sessionId);
						setSession(newAuthoringSession);
					} catch (error) {
						console.error(error, {
							extra: {
								session,
							},
						});
						console.error('Failed to find authoring session:', error);
					} finally {
						setIsFetchingSession(false);
					}
				};

				findAuthoringSession();
			}

			return;
		}

		if (searchParams.has('author')) {
			setIsFetchingSession(true);

			const findOrCreateAuthoringSession = async () => {
				try {
					const authoringSession = await fetchSession('author', pathname.includes('assessment'));
					if (!authoringSession) {
						throw new Error('Failed to create authoring session');
					}

					const authoringSessionData = await authoringSession.json();
					setSession(authoringSessionData);

					if (authoringSessionData.systemPrompt) {
						const name = authoringSessionData.systemPrompt.prompt.characterName.toLowerCase();

						setAuthoringCharacter(isCharacterNameValid(name) ? name : 'maya');
					}
				} catch (error) {
					console.error(error, {
						extra: {
							authorCharacter: authoringCharacter,
						},
					});
					console.error('Failed to create authoring session:', error);
				} finally {
					setIsFetchingSession(false);
				}
			};

			findOrCreateAuthoringSession();
		}
	}, [pathname, sessionId]);

	useEffect(() => {
		if (isFetchingSession) return;
		if (!coachingSession || !coachingSessionId || coachingSession.id !== coachingSessionId) return;

		if (pathname.startsWith('/coach/voice')) routeByVoiceCoachingSessionStage(coachingSession?.stage);
	}, [coachingSession?.stage, isFetchingSession]);

	useEffect(() => {
		if (isFetchingSession) {
			return;
		}

		if (!coachingSessionId) return;

		if (pathname.startsWith('/coach/voice')) {
			setIsFetchingSession(true);

			const findCoachingSession = async () => {
				try {
					const newCoachingSession = await getCoachingSession(coachingSessionId);
					setCoachingSession(newCoachingSession);
				} catch (error) {
					console.error(error, {
						extra: {
							coachingSession,
						},
					});
					console.error('Failed to create coaching session:', error);
				} finally {
					setIsFetchingSession(false);
				}
			};

			findCoachingSession();
		}
	}, [coachingSessionId, pathname]);

	return (
		<SessionProvider>
			<SessionContext.Provider value={sessionContextValue}>{children}</SessionContext.Provider>
		</SessionProvider>
	);
}
